import React, { useState } from "react";
import {
  ShieldPlus, CheckCircle, XCircle, MapPin, Phone, Buildings, Wallet,
  DownloadSimple, Question, FilePdf, Users, UserCircle, Eye, Funnel,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadFile } from "@/lib/api";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startPayment } from "@/lib/razorpay";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

async function grab(path, filename) {
  try {
    await downloadFile(path, filename);
  } catch (e) {
    toast.error("Download failed. Please try again.");
  }
}

async function preview(path) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    window.open(url, "_blank", "noopener");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error("Preview failed. Please try again.");
  }
}

const statusColors = {
  Submitted: "bg-blue-100 text-blue-800",
  "Under Review": "bg-amber-100 text-amber-800",
  Approved: "bg-emerald-100 text-emerald-800",
  Settled: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-red-100 text-red-800",
};

export function EHealthCard({ data }) {
  return (
    <div
      data-testid="ecard-widget"
      className="relative w-full max-w-sm rounded-2xl p-5 text-white overflow-hidden grain shadow-[0_8px_30px_rgba(0,0,0,0.15)]"
      style={{ background: "linear-gradient(140deg, #065f46 0%, #064E3B 55%, #052e22 100%)" }}
    >
      <div className="relative z-10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldPlus size={22} weight="fill" className="text-emerald-300" />
          <span className="font-heading font-bold tracking-tight">Aarogya Assist</span>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-300">
          {data.plan_name}
        </span>
      </div>

      <div className="relative z-10 mt-6 card-chip w-11 h-8" />

      <div className="relative z-10 mt-4 flex justify-between items-start">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-emerald-200/70">Member ID</p>
          <p className="font-mono text-lg tracking-wider">{data.member_id}</p>
        </div>
        {data.uhid ? (
          <div className="text-right" data-testid="ecard-uhid">
            <p className="text-[10px] uppercase tracking-widest text-emerald-200/70">UHID</p>
            <p className="font-mono text-lg tracking-wider">{data.uhid}</p>
          </div>
        ) : null}
      </div>

      <div className="relative z-10 mt-4 flex justify-between items-end gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-emerald-200/70">Member Name</p>
          <p className="font-heading font-semibold text-base truncate">{data.name}</p>
          <p className="text-xs text-emerald-200/70 mt-0.5 truncate">{data.company}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-widest text-emerald-200/70">Sum Insured</p>
          <p className="font-mono text-sm">{inr(data.sum_insured)}</p>
          {data.balance_sum_insured != null && (
            <p data-testid="ecard-balance" className="font-mono text-[11px] text-emerald-300 mt-0.5">Balance {inr(data.balance_sum_insured)}</p>
          )}
        </div>
      </div>

      <div className="relative z-10 mt-4 flex justify-between text-[11px] text-emerald-200/70 font-mono border-t border-white/10 pt-3">
        <span>{data.policy_number}</span>
        <span>Valid till {data.valid_till}</span>
      </div>
      {data.opd_cover != null && (
        <div data-testid="ecard-opd-cover" className="relative z-10 mt-2 flex justify-between text-[11px] text-emerald-200/70 font-mono">
          <span>OPD Cover</span>
          <span className="text-emerald-300">{inr(data.opd_cover)}</span>
        </div>
      )}

      <button
        data-testid="ecard-download"
        onClick={() => grab("/ecard/image", `ecard_${data.member_id}.png`)}
        className="relative z-10 mt-4 w-full flex items-center justify-center gap-2 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-medium py-2 transition-colors"
      >
        <DownloadSimple size={15} /> Download e-card image
      </button>
    </div>
  );
}

export function FamilyECardsCard({ data }) {
  if (!data || data.length === 0) {
    return (
      <div data-testid="family-ecards-widget" className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 shadow-sm text-center">
        <ShieldPlus size={30} weight="duotone" className="text-emerald-600 mx-auto" />
        <p className="mt-2 text-sm text-zinc-600">No family e-cards yet. Buy a Family Floater OPD plan to add covered members.</p>
      </div>
    );
  }
  return (
    <div data-testid="family-ecards-widget" className="w-full max-w-sm space-y-3">
      {data.map((d, idx) => (
        <div key={idx} data-testid={`family-ecard-${idx}`}
          className="relative w-full rounded-2xl p-5 text-white overflow-hidden grain shadow-[0_8px_30px_rgba(0,0,0,0.15)]"
          style={{ background: "linear-gradient(140deg, #0e7490 0%, #0c4a6e 55%, #082f49 100%)" }}>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldPlus size={20} weight="fill" className="text-cyan-200" />
              <span className="font-heading font-bold tracking-tight">Aarogya Assist · Family</span>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-cyan-200">{d.plan_name}</span>
          </div>
          <div className="relative z-10 mt-4">
            <p className="text-[10px] uppercase tracking-widest text-cyan-100/70">Member ID</p>
            <p className="font-mono text-base tracking-wider">{d.member_id}</p>
          </div>
          <div className="relative z-10 mt-3 flex justify-between items-end">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-cyan-100/70">Name</p>
              <p className="font-heading font-semibold text-base">{d.name}</p>
              <p className="text-xs text-cyan-100/70 mt-0.5">{d.relation}{d.age ? ` · ${d.age} yrs` : ""}{d.company ? ` · ${d.company}` : ""}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-cyan-100/70">OPD Cover</p>
              <p className="font-mono text-sm">{inr(d.cover_amount)}</p>
              {d.opd_balance != null && (
                <p data-testid={`family-ecard-balance-${idx}`} className="font-mono text-[11px] text-cyan-200 mt-0.5">Balance {inr(d.opd_balance)}</p>
              )}
            </div>
          </div>
          {d.valid_till ? (
            <div className="relative z-10 mt-3 text-[11px] text-cyan-100/70 font-mono border-t border-white/10 pt-2">Valid till {new Date(d.valid_till).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
          ) : null}
          <button
            data-testid={`family-ecard-download-${idx}`}
            onClick={() => grab(`/opd/family-card/image?member_id=${encodeURIComponent(d.member_id)}`, `family_ecard_${d.member_id}.png`)}
            className="relative z-10 mt-3 w-full flex items-center justify-center gap-2 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-medium py-2 transition-colors">
            <DownloadSimple size={15} /> Download / share card
          </button>
        </div>
      ))}
    </div>
  );
}


export function PolicyCard({ data }) {
  const remaining = (data.sum_insured || 0) - (data.used_amount || 0);
  const pct = Math.min(100, Math.round(((data.used_amount || 0) / (data.sum_insured || 1)) * 100));
  return (
    <div data-testid="policy-widget" className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600">{data.plan_name}</p>
          <p className="font-mono text-xs text-zinc-500 mt-1">{data.policy_number}</p>
        </div>
        <ShieldPlus size={26} weight="duotone" className="text-emerald-700" />
      </div>

      <div className="mt-4 rounded-lg bg-emerald-50 p-3">
        <div className="flex justify-between text-xs text-zinc-600">
          <span>Used {inr(data.used_amount)}</span>
          <span className="font-semibold text-emerald-800">{inr(remaining)} left</span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-emerald-100 overflow-hidden">
          <div className="h-full bg-emerald-600 rounded-full transition-[width] duration-500" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-1.5 text-[11px] text-zinc-500">Sum insured {inr(data.sum_insured)} · Room: {data.room_rent_limit}</p>
        {data.opd_cover != null && (
          <p data-testid="policy-opd-cover" className="mt-1 text-[11px] text-emerald-700 font-medium">OPD cover: {inr(data.opd_cover)}</p>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {data.coverage?.map((c, i) => (
          <div key={i} className="flex items-start gap-2">
            {c.covered
              ? <CheckCircle size={18} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
              : <XCircle size={18} weight="fill" className="text-zinc-300 mt-0.5 shrink-0" />}
            <div>
              <p className={`text-sm font-medium ${c.covered ? "text-zinc-800" : "text-zinc-400 line-through"}`}>{c.name}</p>
              <p className="text-xs text-zinc-500">{c.detail}</p>
            </div>
          </div>
        ))}
      </div>

      {data.dependents?.length > 0 && (
        <div className="mt-4 border-t border-zinc-100 pt-3">
          <p className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-1">
            <Users size={14} /> Covered dependents
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.dependents.map((d, i) => (
              <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-800">
                {d.name} · {d.relation}
              </span>
            ))}
          </div>
        </div>
      )}

      <button
        data-testid="policy-download"
        onClick={() => grab("/policy/pdf", "policy_certificate.pdf")}
        className="mt-4 w-full flex items-center justify-center gap-2 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white text-xs font-medium py-2.5 transition-colors"
      >
        <FilePdf size={16} /> Download policy PDF
      </button>
    </div>
  );
}

export function ClaimsCard({ data }) {
  const [zoom, setZoom] = useState(null);
  const token = (typeof window !== "undefined" && localStorage.getItem("insurehub_token")) || "";
  const billUrl = (id) => `${API}/claims/${id}/bill?token=${token}`;
  const claims = Array.isArray(data) ? data : (data?.claims || []);
  const summary = Array.isArray(data) ? null : (data?.summary || null);
  const SummaryHeader = summary ? (
    <div data-testid="claims-summary" className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-xs">
      <div className="flex justify-between"><span className="text-zinc-500">Policy number</span><span className="font-mono font-medium text-emerald-900">{summary.policy_number || "—"}</span></div>
      {summary.valid_till ? <div className="flex justify-between mt-1"><span className="text-zinc-500">Valid till</span><span className="font-medium text-emerald-900">{summary.valid_till}</span></div> : null}
      <div className="flex justify-between mt-1"><span className="text-zinc-500">Sum insured</span><span className="font-mono font-medium text-emerald-900">{inr(summary.sum_insured)}</span></div>
      <div className="flex justify-between mt-1"><span className="text-zinc-500">Balance sum insured</span><span data-testid="claims-balance" className="font-mono font-semibold text-emerald-700">{inr(summary.balance_sum_insured)}</span></div>
      {summary.opd_cover != null ? <div className="flex justify-between mt-1"><span className="text-zinc-500">OPD cover</span><span data-testid="claims-opd-cover" className="font-mono font-medium text-emerald-900">{inr(summary.opd_cover)}</span></div> : null}
    </div>
  ) : null;
  if (!claims || claims.length === 0) {
    return (
      <div data-testid="claims-widget" className="w-full max-w-sm space-y-3">
        {SummaryHeader}
        <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm text-center">
          <Wallet size={30} weight="duotone" className="text-emerald-600 mx-auto" />
          <p className="mt-2 text-sm text-zinc-600">You have no claims yet. Tap "Submit a Claim" to file one.</p>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="claims-widget" className="w-full max-w-sm space-y-3">
      {SummaryHeader}
      {claims.map((c) => (
        <div key={c.id || c.claim_number} className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-zinc-500">{c.claim_number}</span>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusColors[c.status] || "bg-zinc-100 text-zinc-700"}`}>
              {c.status}
            </span>
          </div>
          <div className="mt-2 flex gap-3">
            {c.bill_file_id && (
              <img
                src={billUrl(c.id)}
                alt="Your bill"
                data-testid={`my-claim-bill-${c.claim_number}`}
                onClick={() => setZoom(billUrl(c.id))}
                className="h-14 w-14 shrink-0 rounded-md object-cover border border-zinc-200 cursor-pointer hover:ring-2 hover:ring-emerald-400 transition"
              />
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-zinc-800">{c.treatment}</p>
              <p className="text-xs text-zinc-500">{c.hospital}</p>
            </div>
          </div>
          <div className="mt-2 flex justify-between items-center">
            <span className="text-[11px] text-zinc-400">{c.claim_type} · {c.admission_date || "—"}</span>
            <span className="font-mono text-sm font-semibold text-emerald-800">{inr(c.amount)}</span>
          </div>
          {c.review_flag && (
            <p data-testid={`my-claim-review-${c.claim_number}`} className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
              ⚠ The amount you entered doesn't match your bill — our team may reach out for a correction.
            </p>
          )}
        </div>
      ))}
      {zoom && (
        <div
          data-testid="my-bill-zoom-overlay"
          onClick={() => setZoom(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
        >
          <div className="relative max-h-[90vh] max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="my-bill-zoom-close"
              onClick={() => setZoom(null)}
              className="absolute -right-3 -top-3 h-8 w-8 rounded-full bg-white text-zinc-800 shadow flex items-center justify-center hover:bg-zinc-100"
            >
              ✕
            </button>
            <img src={zoom} alt="Bill full" className="max-h-[90vh] max-w-full rounded-lg object-contain bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}

export function HospitalsCard({ data }) {
  const [filter, setFilter] = useState("All");
  const insurers = Array.from(new Set((data || []).map((h) => h.insurer).filter(Boolean)));
  const shown = (data || []).filter((h) => filter === "All" || h.insurer === filter).slice(0, 12);
  return (
    <div data-testid="hospitals-widget" className="w-full max-w-sm space-y-3">
      {insurers.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <Funnel size={14} className="text-emerald-700" />
          {["All", ...insurers].map((ins) => (
            <button
              key={ins}
              onClick={() => setFilter(ins)}
              data-testid={`hospital-filter-${ins.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${filter === ins ? "border-emerald-500 bg-emerald-600 text-white" : "border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50"}`}
            >
              {ins}
            </button>
          ))}
        </div>
      )}
      {shown.map((h, i) => (
        <div key={i} className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2">
              <Buildings size={20} weight="duotone" className="text-emerald-700 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-zinc-800">{h.name}</p>
                <p className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                  <MapPin size={12} /> {h.area}, {h.city}
                </p>
              </div>
            </div>
            {h.cashless && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 shrink-0">
                Cashless
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {h.specialties?.slice(0, 3).map((s, j) => (
              <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">{s}</span>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between">
            <p className="text-[11px] text-zinc-400 flex items-center gap-1">
              <Phone size={12} /> {h.phone}
            </p>
            {h.insurer && <span className="text-[10px] text-emerald-700 font-medium">{h.insurer}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function FaqsCard({ data }) {
  if (!data?.length) {
    return (
      <div data-testid="faqs-widget" className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 text-sm text-zinc-600">
        No FAQs available right now.
      </div>
    );
  }
  return (
    <div data-testid="faqs-widget" className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-2 shadow-sm">
      <Accordion type="single" collapsible className="w-full">
        {data.map((f, i) => (
          <AccordionItem key={f.id || i} value={`faq-${i}`} className="border-zinc-100">
            <AccordionTrigger data-testid={`faq-item-${i}`} className="text-left text-sm font-medium text-zinc-800 px-2 hover:no-underline">
              <span className="flex items-center gap-2">
                <Question size={16} weight="duotone" className="text-emerald-600 shrink-0" />
                {f.question}
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-2 text-sm text-zinc-600 leading-relaxed">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 inline-block mb-1 mr-2">{f.category}</span>
              {f.product && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{f.product}</span>}
              <p className="mt-1">{f.answer}</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export function DocumentsCard({ data }) {
  return (
    <div data-testid="documents-widget" className="w-full max-w-sm space-y-2.5">
      {/* Auto-generated */}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Instant downloads</p>
        <div className="flex items-center gap-2 rounded-lg bg-white border border-emerald-200 p-3 mb-2">
          <span className="flex items-center gap-2 text-sm font-medium text-zinc-800 flex-1 min-w-0"><FilePdf size={18} className="text-red-500 shrink-0" /> Policy certificate (PDF)</span>
          <button data-testid="doc-policy-preview" onClick={() => preview("/policy/pdf")} title="Preview" className="p-1.5 rounded-md hover:bg-emerald-50 text-emerald-700 transition-colors"><Eye size={16} /></button>
          <button data-testid="doc-policy-pdf" onClick={() => grab("/policy/pdf", "policy_certificate.pdf")} title="Download" className="p-1.5 rounded-md hover:bg-emerald-50 text-emerald-700 transition-colors"><DownloadSimple size={16} /></button>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-white border border-emerald-200 p-3">
          <span className="flex items-center gap-2 text-sm font-medium text-zinc-800 flex-1 min-w-0"><ShieldPlus size={18} className="text-emerald-600 shrink-0" /> E-health card (image)</span>
          <button data-testid="doc-ecard-preview" onClick={() => preview("/ecard/image")} title="Preview" className="p-1.5 rounded-md hover:bg-emerald-50 text-emerald-700 transition-colors"><Eye size={16} /></button>
          <button data-testid="doc-ecard-img" onClick={() => grab("/ecard/image", "ecard.png")} title="Download" className="p-1.5 rounded-md hover:bg-emerald-50 text-emerald-700 transition-colors"><DownloadSimple size={16} /></button>
        </div>
      </div>

      {/* Uploaded documents */}
      {data?.length > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Shared by your HR</p>
          {data.map((doc) => (
            <div
              key={doc.id}
              data-testid={`doc-uploaded-${doc.id}`}
              className="flex items-center gap-2 rounded-lg border border-zinc-200 p-3 mb-2 last:mb-0"
            >
              <span className="flex items-center gap-2 text-sm text-zinc-800 min-w-0 flex-1">
                <FilePdf size={18} className="text-zinc-400 shrink-0" />
                <span className="truncate">{doc.filename}</span>
              </span>
              <button onClick={() => preview(`/documents/${doc.id}/download`)} title="Preview" className="p-1.5 rounded-md hover:bg-zinc-50 text-emerald-700 transition-colors"><Eye size={16} /></button>
              <button onClick={() => grab(`/documents/${doc.id}/download`, doc.filename)} title="Download" className="p-1.5 rounded-md hover:bg-zinc-50 text-emerald-700 transition-colors"><DownloadSimple size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DependentsCard({ data }) {
  if (!data?.length) {
    return (
      <div data-testid="dependents-widget" className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 text-sm text-zinc-600">
        No dependents are added to your policy yet. Contact your HR admin to add family members.
      </div>
    );
  }
  return (
    <div data-testid="dependents-widget" className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-4 shadow-sm space-y-2">
      <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1 mb-1">
        <Users size={14} /> Covered family members
      </p>
      {data.map((d, i) => (
        <div key={d.id || i} className="flex items-center gap-3 rounded-lg bg-zinc-50 p-3">
          <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
            <UserCircle size={22} weight="duotone" className="text-emerald-700" />
          </div>
          <div>
            <p className="text-sm font-medium text-zinc-800">{d.name}</p>
            <p className="text-xs text-zinc-500">{d.relation} · {d.age} yrs</p>
          </div>
        </div>
      ))}
    </div>
  );
}


const ticketStatusColors = {
  Open: "bg-amber-100 text-amber-800",
  "In Progress": "bg-blue-100 text-blue-800",
  Resolved: "bg-emerald-100 text-emerald-800",
};
const bookingStatusColors = {
  Requested: "bg-amber-100 text-amber-800",
  Scheduled: "bg-blue-100 text-blue-800",
  Completed: "bg-emerald-100 text-emerald-800",
  Cancelled: "bg-red-100 text-red-800",
};

export function TicketsCard({ data }) {
  if (!data || data.length === 0) {
    return (
      <div data-testid="tickets-widget" className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 shadow-sm text-center">
        <Question size={30} weight="duotone" className="text-emerald-600 mx-auto" />
        <p className="mt-2 text-sm text-zinc-600">You have no service tickets yet. Tap "Raise a ticket" to create one.</p>
      </div>
    );
  }
  return (
    <div data-testid="tickets-widget" className="w-full max-w-sm space-y-3">
      {data.map((t) => (
        <div key={t.id || t.ticket_number} className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-zinc-500">{t.ticket_number}</span>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${ticketStatusColors[t.status] || "bg-zinc-100 text-zinc-700"}`}>{t.status}</span>
          </div>
          <p className="mt-2 text-xs font-semibold text-emerald-800">{t.category}</p>
          <p className="text-sm text-zinc-700">{t.description}</p>
          {t.resolution_note ? <p className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1">HR: {t.resolution_note}</p> : null}
        </div>
      ))}
    </div>
  );
}

export function BookingsCard({ data }) {
  if (!data || data.length === 0) {
    return (
      <div data-testid="bookings-widget" className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 shadow-sm text-center">
        <Buildings size={30} weight="duotone" className="text-emerald-600 mx-auto" />
        <p className="mt-2 text-sm text-zinc-600">No home check-ups booked yet. Tap "Book check-up" to schedule one with Lupin Diagnostics.</p>
      </div>
    );
  }
  return (
    <div data-testid="bookings-widget" className="w-full max-w-sm space-y-3">
      {data.map((b) => (
        <div key={b.id || b.booking_number} className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-zinc-500">{b.booking_number}</span>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${bookingStatusColors[b.status] || "bg-zinc-100 text-zinc-700"}`}>{b.status}</span>
          </div>
          <p className="mt-2 text-sm font-semibold text-zinc-800">🩺 Home collection · Lupin Diagnostics</p>
          <p className="text-xs text-zinc-500">{b.preferred_date} · {b.preferred_time}</p>
          <p className="text-[11px] text-zinc-400 mt-1">{b.address}, {b.pincode}</p>
          {b.vendor_remarks ? <p className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1">Lupin: {b.vendor_remarks}</p> : null}
          {b.report_file_id ? (
            <button
              data-testid={`download-report-${b.booking_number}`}
              onClick={() => grab(`/bookings/${b.id}/report`, b.report_filename || `${b.booking_number}-report.pdf`)}
              className="mt-3 inline-flex items-center gap-2 w-full justify-center rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-2 transition-colors">
              <DownloadSimple size={14} weight="bold" /> Download report
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}


export function OPDPlansCard({ data }) {
  const [paidPlan, setPaidPlan] = useState(null);
  const [busy, setBusy] = useState("");
  const [floaterPlan, setFloaterPlan] = useState(null);
  const [members, setMembers] = useState([{ name: "", relation: "Spouse", age: "" }]);
  if (!data || data.length === 0) {
    return (
      <div data-testid="opd-plans-widget" className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 shadow-sm text-center">
        <ShieldPlus size={30} weight="duotone" className="text-emerald-600 mx-auto" />
        <p className="mt-2 text-sm text-zinc-600">No OPD plans available yet. Please check back soon.</p>
      </div>
    );
  }
  const pay = async (plan, coverage, mem) => {
    setBusy(`${plan.id}-${coverage}`);
    const res = await startPayment({ type: "opd", item_id: plan.id, coverage_type: coverage, members: mem });
    setBusy("");
    if (res) { setPaidPlan({ id: plan.id, payment_id: res.payment_id }); setFloaterPlan(null); }
  };
  const setMember = (i, k, v) => setMembers((m) => m.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const inputCls = "h-8 rounded-md border border-zinc-200 px-2 text-xs w-full";
  return (
    <div data-testid="opd-plans-widget" className="w-full max-w-md space-y-3">
      {data.map((p) => (
        <div key={p.id} data-testid={`opd-plan-${p.name}`} className="rounded-xl border border-emerald-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="font-heading font-semibold text-emerald-950">{p.name} OPD Cover</p>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{inr(p.cover_amount)} cover</span>
          </div>
          {p.value_prop ? <p className="text-[11px] text-zinc-500 mt-0.5">Annual value {p.value_prop}</p> : null}
          {p.benefits_summary ? <p data-testid={`opd-benefits-summary-${p.name}`} className="text-xs text-zinc-600 mt-2 bg-emerald-50/60 border border-emerald-100 rounded-lg px-2.5 py-1.5 leading-relaxed">{p.benefits_summary}</p> : null}
          <ul className="mt-2 space-y-1">
            {(p.benefits || []).slice(0, 5).map((b, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-zinc-600">
                <CheckCircle size={14} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
                <span>{b.benefit}{b.limit && b.limit !== "Included" ? ` — ${b.limit}` : ""}</span>
              </li>
            ))}
            {(p.benefits || []).length > 5 ? <li className="text-[11px] text-zinc-400 pl-5">+ {p.benefits.length - 5} more benefits</li> : null}
          </ul>
          {paidPlan?.id === p.id ? (
            <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid={`opd-paid-${p.name}`}>
              <p>✓ Plan activated! Check WhatsApp/email for confirmation.</p>
              <button onClick={() => grab(`/payments/${paidPlan.payment_id}/receipt`, `receipt-${p.name}.pdf`)} data-testid={`opd-receipt-${p.name}`}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-800 hover:underline">
                <DownloadSimple size={13} weight="bold" /> Download receipt
              </button>
            </div>
          ) : (p.price_individual > 0 || p.price_floater > 0) ? (
            <div className="mt-3">
              <div className="grid grid-cols-2 gap-2">
                {p.price_individual > 0 ? (
                  <button data-testid={`opd-buy-individual-${p.name}`} disabled={!!busy} onClick={() => pay(p, "individual")}
                    className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-2 transition-colors disabled:opacity-60">
                    {busy === `${p.id}-individual` ? "Opening…" : `Individual · ${inr(p.price_individual)}`}
                  </button>
                ) : null}
                {p.price_floater > 0 ? (
                  <button data-testid={`opd-buy-floater-${p.name}`} disabled={!!busy}
                    onClick={() => { setFloaterPlan(floaterPlan === p.id ? null : p.id); setMembers([{ name: "", relation: "Spouse", age: "" }]); }}
                    className="rounded-lg border border-emerald-300 text-emerald-800 hover:bg-emerald-50 text-xs font-medium px-3 py-2 transition-colors disabled:opacity-60">
                    {`Family · ${inr(p.price_floater)}`}
                  </button>
                ) : null}
              </div>
              {floaterPlan === p.id ? (
                <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50/60 p-3" data-testid={`opd-floater-form-${p.name}`}>
                  <p className="text-[11px] font-semibold text-emerald-800 mb-2">Add family members (2 Adults + up to 4 Children)</p>
                  {members.map((mem, i) => (
                    <div key={i} className="grid grid-cols-12 gap-1.5 mb-1.5">
                      <input data-testid={`opd-member-name-${i}`} value={mem.name} onChange={(e) => setMember(i, "name", e.target.value)}
                        placeholder="Full name" className={`${inputCls} col-span-5`} />
                      <select data-testid={`opd-member-relation-${i}`} value={mem.relation} onChange={(e) => setMember(i, "relation", e.target.value)}
                        className={`${inputCls} col-span-4`}>
                        {["Spouse", "Son", "Daughter", "Father", "Mother", "Other"].map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <input data-testid={`opd-member-age-${i}`} value={mem.age} onChange={(e) => setMember(i, "age", e.target.value)}
                        placeholder="Age" className={`${inputCls} col-span-2`} />
                      <button type="button" onClick={() => setMembers((m) => m.filter((_, idx) => idx !== i))}
                        className="col-span-1 text-red-400 hover:text-red-600 text-sm">×</button>
                    </div>
                  ))}
                  {members.length < 6 ? (
                    <button type="button" data-testid={`opd-add-member-${p.name}`} onClick={() => setMembers((m) => [...m, { name: "", relation: "Son", age: "" }])}
                      className="text-[11px] text-emerald-700 hover:underline">+ Add another member</button>
                  ) : null}
                  <button data-testid={`opd-floater-pay-${p.name}`} disabled={!!busy}
                    onClick={() => { const valid = members.filter((m) => m.name.trim()); if (!valid.length) { toast.error("Add at least one family member."); return; } pay(p, "floater", valid); }}
                    className="mt-2 w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-2 transition-colors disabled:opacity-60">
                    {busy === `${p.id}-floater` ? "Opening…" : `Pay ${inr(p.price_floater)} for family`}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="mt-3 text-[11px] text-zinc-400">Pricing coming soon — please contact HR to enroll.</p>
          )}
        </div>
      ))}
      <OPDFaqEnquiry />
    </div>
  );
}

const OPD_FAQS = [
  { q: "What does an OPD plan cover?", a: "Doctor consultations, prescribed diagnostics & pharmacy, dental, vision, teleconsultation, mental wellness and more — up to your plan's annual limits." },
  { q: "Cashless or reimbursement?", a: "Most benefits are cashless at network providers. Where cashless isn't available you can pay and claim reimbursement in the app." },
  { q: "Does it cover my family?", a: "Yes — pick the Family Floater option while buying and add your covered members (2 adults + up to 4 children)." },
  { q: "How soon is my plan active?", a: "Instantly after a successful payment. You'll get a confirmation on WhatsApp and email with your receipt." },
];

function OPDFaqEnquiry() {
  const { user } = useAuth();
  const [faqs, setFaqs] = useState(OPD_FAQS.map((f) => ({ question: f.q, answer: f.a })));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", mobile: user?.mobile || "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    api.get("/opd/faqs").then((r) => { if (r.data?.length) setFaqs(r.data); }).catch(() => {});
  }, []);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Please enter your name."); return; }
    if (form.mobile.replace(/\D/g, "").length < 10) { toast.error("Enter a valid 10-digit mobile number."); return; }
    setBusy(true);
    try {
      await api.post("/callbacks", { name: form.name.trim(), email: form.email.trim(), mobile: form.mobile.trim(), reason: "OPD plan enquiry" });
      setDone(true);
      toast.success("Thanks! Our team will call you back about OPD plans.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const inputCls = "h-9 w-full rounded-md border border-zinc-200 px-2 text-sm";
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm" data-testid="opd-faq-enquiry">
      <p className="font-heading font-semibold text-emerald-950 text-sm">OPD — Frequently asked questions</p>
      <div className="mt-2 divide-y divide-zinc-100">
        {faqs.map((f, i) => (
          <details key={i} className="py-2" data-testid={`opd-faq-${i}`}>
            <summary className="cursor-pointer text-sm text-emerald-900 font-medium list-none flex justify-between items-center">
              {f.question}<span className="text-emerald-500">+</span>
            </summary>
            <p className="mt-1 text-xs text-zinc-600">{f.answer}</p>
          </details>
        ))}
      </div>
      <a href="https://www.aarogya-assist.com/opd" target="_blank" rel="noopener noreferrer"
        data-testid="opd-know-more" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline">
        More information & book →
      </a>
      <div className="mt-3 border-t border-zinc-100 pt-3">
        {done ? (
          <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="opd-enquiry-done">
            ✓ Thanks {form.name.split(" ")[0]}! Our team will call you on {form.mobile} shortly.
          </p>
        ) : !open ? (
          <button data-testid="opd-enquiry-open" onClick={() => setOpen(true)}
            className="w-full rounded-lg border border-emerald-300 text-emerald-800 hover:bg-emerald-50 text-sm font-medium px-3 py-2 transition-colors">
            Have questions? Request a callback
          </button>
        ) : (
          <div className="space-y-2" data-testid="opd-enquiry-form">
            <p className="text-xs text-zinc-500">Leave your details and our team will call you back about OPD plans.</p>
            <input data-testid="opd-enquiry-name" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <input data-testid="opd-enquiry-email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            <input data-testid="opd-enquiry-mobile" className={inputCls} placeholder="Mobile number" value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} />
            <button data-testid="opd-enquiry-submit" disabled={busy} onClick={submit}
              className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3 py-2 transition-colors disabled:opacity-60">
              {busy ? "Submitting…" : "Request a callback"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function CheckupPackagesCard({ data }) {
  const [paidPkg, setPaidPkg] = useState(null);
  const [busy, setBusy] = useState("");
  if (!data || data.length === 0) {
    return (
      <div data-testid="checkup-packages-widget" className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 shadow-sm text-center">
        <ShieldPlus size={30} weight="duotone" className="text-emerald-600 mx-auto" />
        <p className="mt-2 text-sm text-zinc-600">No health check-up packages available yet.</p>
      </div>
    );
  }
  const pay = async (pkg) => {
    setBusy(pkg.id);
    const res = await startPayment({ type: "checkup", item_id: pkg.id });
    setBusy("");
    if (res) setPaidPkg({ id: pkg.id, payment_id: res.payment_id });
  };
  return (
    <div data-testid="checkup-packages-widget" className="w-full max-w-md space-y-3">
      {data.map((p) => (
        <div key={p.id} data-testid={`checkup-pkg-${p.name}`} className="rounded-xl border border-emerald-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="font-heading font-semibold text-emerald-950">{p.name} · {p.params} parameters</p>
            <span className="text-sm font-bold text-emerald-700">{inr(p.price)}</span>
          </div>
          {p.includes ? <p className="text-[11px] text-emerald-700 mt-0.5">{p.includes}</p> : null}
          <ul className="mt-2 space-y-1">
            {(p.tests || []).slice(0, 6).map((t, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-zinc-600">
                <CheckCircle size={14} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" /><span>{t}</span>
              </li>
            ))}
            {(p.tests || []).length > 6 ? <li className="text-[11px] text-zinc-400 pl-5">+ {p.tests.length - 6} more tests</li> : null}
          </ul>
          {paidPkg?.id === p.id ? (
            <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid={`checkup-paid-${p.name}`}>
              <p>✓ Paid! We've sent you a link to share your address & slot for home collection.</p>
              <button onClick={() => grab(`/payments/${paidPkg.payment_id}/receipt`, `receipt-${p.name}.pdf`)} data-testid={`checkup-receipt-${p.name}`}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-800 hover:underline">
                <DownloadSimple size={13} weight="bold" /> Download receipt
              </button>
            </div>
          ) : (
            <button data-testid={`checkup-buy-${p.name}`} disabled={!!busy} onClick={() => pay(p)}
              className="mt-3 inline-flex items-center justify-center gap-2 w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2.5 transition-colors disabled:opacity-60">
              {busy === p.id ? "Opening payment…" : `Select & Pay ${inr(p.price)}`}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}


export function MyOPDCard({ data }) {
  if (!data || data.length === 0) {
    return (
      <div data-testid="my-opd-widget" className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 shadow-sm text-center">
        <ShieldPlus size={30} weight="duotone" className="text-emerald-600 mx-auto" />
        <p className="mt-2 text-sm text-zinc-600">You don't have an active OPD plan yet. Ask "OPD plans" to buy one.</p>
      </div>
    );
  }
  return (
    <div data-testid="my-opd-widget" className="w-full max-w-md space-y-3">
      {data.map((e) => (
        <div key={e.id} data-testid={`my-opd-${e.plan_id}`} className="rounded-xl border border-emerald-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="font-heading font-semibold text-emerald-950">{e.plan_name}</p>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Active</span>
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">
            Cover {inr(e.cover_amount)} · Paid {inr(e.amount)}
            {e.created_at ? ` · since ${new Date(e.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : ""}
          </p>
          {(e.members || []).length ? (
            <p className="text-[11px] text-emerald-700 mt-1">Family covered: {e.members.map((m) => `${m.name}${m.relation ? ` (${m.relation})` : ""}`).join(", ")}</p>
          ) : null}
          <p className="mt-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">Your benefit limits</p>
          <ul className="mt-1 space-y-1">
            {(e.benefits || []).map((b, i) => (
              <li key={i} className="flex items-start justify-between gap-2 text-xs text-zinc-600 border-b border-zinc-50 py-1">
                <span className="flex items-start gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />{b.benefit}</span>
                {b.trackable ? (
                  <span className="text-right whitespace-nowrap">
                    <span className="font-semibold text-emerald-800">{b.unit === "₹" ? inr(b.remaining) : `${b.remaining}`}</span>
                    <span className="text-zinc-400"> {b.unit === "₹" ? "left" : "sessions left"}</span>
                    <span className="block text-[10px] text-zinc-400">of {b.unit === "₹" ? inr(b.limit_value) : `${b.limit_value} sessions`} · used {b.unit === "₹" ? inr(b.used) : b.used}</span>
                  </span>
                ) : (
                  <span className="font-semibold text-emerald-800 whitespace-nowrap">{b.limit}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}


export function MemberLookupCard() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async (e) => {
    if (e) e.preventDefault();
    if (!query.trim()) { toast.error("Enter an employee code or phone number."); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.get(`/lookup/member?query=${encodeURIComponent(query.trim())}`);
      setResult(data);
    } catch (err) {
      setResult(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No member found");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="member-lookup-widget" className="w-full max-w-sm rounded-xl border border-emerald-200 bg-white p-4 shadow-sm">
      <p className="font-heading font-semibold text-emerald-950 text-sm">Look up a member</p>
      <p className="text-xs text-zinc-500 mb-2">Search by employee code, phone, member ID or UHID.</p>
      <form onSubmit={search} className="flex gap-2">
        <input data-testid="member-lookup-input" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. EMP1001 or 9876543210"
          className="h-9 flex-1 rounded-md border border-zinc-200 px-2 text-sm" />
        <button type="submit" data-testid="member-lookup-submit" disabled={busy}
          className="h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-4 transition-colors disabled:opacity-60">
          {busy ? "…" : "Look up"}
        </button>
      </form>
      {result && (
        <div data-testid="member-lookup-card-result" className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50/60 p-3">
          <p className="font-heading font-semibold text-emerald-950">{result.name}</p>
          <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-zinc-600">
            {result.employee_code ? <p>Code: <b className="text-zinc-800">{result.employee_code}</b></p> : null}
            <p>Member ID: <b className="text-zinc-800">{result.member_id}</b></p>
            {result.uhid ? <p>UHID: <b className="text-zinc-800">{result.uhid}</b></p> : null}
            <p>Plan: <b className="text-zinc-800">{result.plan_name}</b></p>
            <p>Sum insured: <b className="text-zinc-800">{inr(result.sum_insured)}</b></p>
          </div>
          {result.dependents?.length ? (
            <div className="mt-2 border-t border-emerald-100 pt-2">
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">Family covered</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {result.dependents.map((d, i) => (
                  <span key={i} className="text-[11px] bg-white border border-emerald-100 rounded-full px-2 py-0.5 text-emerald-800">
                    {d.name} · {d.relation}
                  </span>
                ))}
              </div>
            </div>
          ) : <p className="mt-2 text-xs text-zinc-400">No family members mapped.</p>}
        </div>
      )}
    </div>
  );
}

export function PolicyTermsCard({ data }) {
  const terms = (data && data.terms) || "";
  return (
    <div data-testid="policy-terms-widget" className="w-full max-w-md rounded-xl border border-emerald-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <FilePdf size={18} weight="duotone" className="text-emerald-600" />
        <p className="font-heading font-semibold text-emerald-950 text-sm">Policy Terms & Conditions</p>
      </div>
      {data?.policy_number ? <p className="text-[11px] text-zinc-500 mt-0.5 font-mono">Policy {data.policy_number}</p> : null}
      {data?.valid_till ? <p data-testid="policy-terms-expiry" className="text-[11px] text-emerald-700 mt-0.5 font-medium">📅 Policy valid till: {data.valid_till}</p> : null}
      {terms ? (
        <p data-testid="policy-terms-text" className="mt-2 text-xs text-zinc-700 whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto">{terms}</p>
      ) : (
        <p className="mt-2 text-xs text-zinc-400">No terms & conditions are on file for your policy yet. Please contact HR.</p>
      )}
    </div>
  );
}
